import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import useApi from '../utils/AxiosWrapper';
import { useUser } from '../utils/UserContext';

interface BrandingContextType {
  // Dashboard white-label logo (TopBar / Sidenav). Null for application-only
  // partners, so their customers see TACH only after approval.
  logoUrl: string | null;
  // Partner logo shown beside the TACH logo on the signup application header.
  applicationLogoUrl: string | null;
  // Fetches /branding/me again and resolves with the new application logo URL.
  // The logo URLs are presigned and expire (BRANDING_LOGO_PRESIGN_TTL_SECONDS),
  // so a header that mounts after that hits a dead URL and needs a fresh one.
  refreshBranding: () => Promise<string | null>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(
  undefined
);

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
}) => {
  const { userId, userType } = useUser();
  const { callProtectedApi } = useApi();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [applicationLogoUrl, setApplicationLogoUrl] = useState<string | null>(
    null
  );

  const fetchBranding = async (): Promise<string | null> => {
    if (!userId || !userType) return null;
    try {
      const response = await callProtectedApi('/branding/me', 'get');
      const nextApplicationLogoUrl =
        response?.data?.data?.application_logo_url ?? null;
      setLogoUrl(response?.data?.data?.logo_url ?? null);
      setApplicationLogoUrl(nextApplicationLogoUrl);
      return nextApplicationLogoUrl;
    } catch (err) {
      Sentry.captureException(err, { tags: { branding: 'fetch-failed' } });
      setLogoUrl(null);
      setApplicationLogoUrl(null);
      return null;
    }
  };

  useEffect(() => {
    fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userType]);

  return (
    <BrandingContext.Provider
      value={{ logoUrl, applicationLogoUrl, refreshBranding: fetchBranding }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextType => {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return ctx;
};

export default BrandingProvider;
